// src/lib/sun.ts — F10-T4: poziția soarelui pe cer (REQ-M3D-009, simularea însoririi).
// Funcții PURE, zero dependințe — algoritmul solar simplificat NOAA/Meeus (precizie ~0.3°,
// arhi-suficientă pentru umbrirea vizuală a fațadelor; NU e calcul de efemeride juridic).
// Folosit de map.client (light.position în MapLibre) + teste pe valori astronomice cunoscute.

const DEG = Math.PI / 180

export interface SunPosition {
	/** Azimutul în grade de la NORD, în sensul acelor de ceas (0=N, 90=E, 180=S, 270=V). */
	azimuth: number
	/** Elevația în grade deasupra orizontului (negativ = soarele e sub orizont / noapte). */
	elevation: number
}

/**
 * Poziția soarelui la un moment absolut, văzută din (lat, lng) WGS84.
 * `date` e un moment ABSOLUT (algoritmul lucrează în UTC prin getTime) — fusul orar e treaba
 * apelantului (UI-ul construiește Date în ora locală a browserului).
 */
export function sun_position(date: Date, lat: number, lng: number): SunPosition {
	// Zile (fracționare) de la epoca J2000.0 (2000-01-01 12:00 UTC).
	const n = date.getTime() / 86400000 - 10957.5
	// Longitudinea medie + anomalia medie a soarelui (grade).
	const l = norm360(280.46 + 0.9856474 * n)
	const g = norm360(357.528 + 0.9856003 * n) * DEG
	// Longitudinea ecliptică + oblicitatea eclipticii.
	const lambda = (l + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * DEG
	const epsilon = (23.439 - 0.0000004 * n) * DEG
	// Ascensia dreaptă + declinația.
	const alpha = Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda))
	const delta = Math.asin(Math.sin(epsilon) * Math.sin(lambda))
	// Timpul sideral local → unghiul orar al soarelui.
	const gmst_deg = norm360(280.46061837 + 360.98564736629 * n)
	const h = norm360(gmst_deg + lng - alpha / DEG) * DEG
	const phi = lat * DEG
	const sin_elev = Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(h)
	const elevation = Math.asin(Math.max(-1, Math.min(1, sin_elev))) / DEG
	// Azimut de la nord, în sensul acelor de ceas.
	const az =
		Math.atan2(Math.sin(h), Math.cos(h) * Math.sin(phi) - Math.tan(delta) * Math.cos(phi)) / DEG
	return { azimuth: norm360(az + 180), elevation }
}

function norm360(deg: number): number {
	return ((deg % 360) + 360) % 360
}

/**
 * Poziția luminii MapLibre pentru un soare dat: `[radial, azimut, polar]` cu `anchor: "map"`
 * (azimutul luminii = azimutul soarelui de la nord; polarul = distanța de zenit). Sub orizont
 * lumina rămâne razantă (polar 90°) — MapLibre n-are „noapte", doar direcția umbririi.
 */
export function sun_to_light_position(sun: SunPosition): [number, number, number] {
	const polar = Math.max(0, Math.min(90, 90 - sun.elevation))
	return [1.15, sun.azimuth, polar]
}
